import React, {Component} from 'react'
import {connect} from 'react-redux'
import {compose} from 'redux'
import {push, replace} from 'react-router-redux'
import {translate} from 'react-i18next'
import {withStyles} from '@material-ui/core/styles'
import withWidth, {isWidthUp} from '@material-ui/core/withWidth'
import ArrowBack from '@material-ui/icons/ArrowBack'
import ArrowForward from '@material-ui/icons/ArrowForward'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import forIn from 'lodash/forIn'
import isFunction from 'lodash/isFunction'
import isInteger from 'lodash/isInteger'
import isObject from 'lodash/isObject'
import mapValues from 'lodash/mapValues'
import noop from 'lodash/noop'

import * as actions from '../../actionTypes'
import {getPathnameEnding, getQueryStrParamVal} from '../../utils'


const styles = theme => {
  const {palette, spacing} = theme
  return {
    iconLeft: {marginRight: spacing.unit},
    iconRight: {marginLeft: spacing.unit},
    linearProgressRoot: {
      backgroundColor: palette.action.disabledBackground
    },
    navButton: {
      backgroundColor: '#fff',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      height: 65,
      width: '100%',
      '&.borderTop': {
        borderTop: `1px solid ${palette.action.disabledBackground}`
      }
    },
    navButtonDivider: {
      borderLeftColor: palette.action.disabledBackground,
      borderStyle: 'solid',
      borderWidth: 0,
      [theme.breakpoints.down('sm')]: {
        borderLeftWidth: 1
      }
    },
    navContainer: {
      maxHeight: 70,
      paddingBottom: 50,
      marginBottom: 50,
      marginTop: 65,
      [theme.breakpoints.down('sm')]: {
        marginBottom: 0,
        marginTop: 0
      }
    }
  }
}

// used to map page numbers to words.
const NUMBERS_AS_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight']

/**
 * Hoc to add navigation capabilities to the counseling session form.
 * @param [config] {Object}
 * @param config.bypass {Object} - Allows us to configure whether forward/back
 *  should skip "bypass" the adjacent page. Each key should correspond to the end
 *  section of a session page pathname; e.g. `top_3_factors`, `enter_options_1`
 * @param config.bypass[pathnameEnd].predicate {Function} - determines if bypass
 *  should occur. Returning true will bypass, while returning false will not.
 *  This function is passed a fresh copy of the counseling session form data at
 *  each invocation.
 * @param [config.bypass[pathnameEnd].onBypass] {Function} - Executes if and
 *  when predicate passes.
 * E.g. to run a bypass check on the `/session/top_3_factors` page, the config
 * could setup as follows:
 * `
 * {
 *   bypass: {
 *      'top_3_factors': {
 *        predicate: (counselingSessionData) => boolean,
 *        onBypass: (counselingSessionData, dispatch) => {}
 *      }
 *   }
 * }
 * `
 * @param [config.cleanup] {function(dispatch)} - called when nav hoc will unmount with arg this.props.dispatch
 * @param lastly {Object} - Configures last forward step of navigation
 * @param lastly.goesTo {string} - destination (pathname) of last forward navigation click
 *  (since we have reached the end of the pages defined in pages array)
 * @param [lastly.beforeNavigation] {function(dispatch)} - invoked before navigation action is dispatched with arg
 *  this.props.dispatch
 */
const withNavigation = (config = {}) => WrappedComponent => {
  class WithNavigation extends Component {

    static DEFAULT_NEXT_LABEL = 'Next'

    constructor(props) {
      super(props)
      this.state = {
        // component display index
        cdi: this.getCdiFromQueryString(props.location.search),
        index: null,
        isBackHidden: false,
        isBackDisabledByConsumer: false,
        isForwardHidden: false,
        isForwardDisabledByConsumer: false,
        forwardButtonLabel: WithNavigation.DEFAULT_NEXT_LABEL,
        hasAmendedNavigation: false,
        stepNumber: null,
        withProgressBar: false,
        displayNavigation: true
      }
      this.isLastDirectionBack = null
      this.pagesToVisit = []
      this.wrappedComponentRef = React.createRef()
    }

    // exposed as navigator prop
    navigator = {
      amendNavigation: handlers => this.amendNavigation(handlers),
      back: () => this.back(),
      disableBack: () => this.setIsBackDisabledByConsumer(true),
      disableForward: () => this.setIsForwardDisabledByConsumer(true),
      enableBack: () => this.setIsBackDisabledByConsumer(false),
      enableForward: () => this.setIsForwardDisabledByConsumer(false),
      forward: () => this.forward(),
      getStepNumber: () => NUMBERS_AS_WORDS[this.state.stepNumber],
      hideBackButton: () => this.setIsBackButtonHidden(true),
      isForwardDisabled: () => this.isForwardDisabled(),
      resetNavigationVisibilityAndLabel: () => this.resetNavigationVisibilityAndLabel(),
      setIsForwardButtonHidden: isHidden => this.setIsForwardButtonHidden(isHidden),
      setForwardButtonLabel: label => this.setForwardButtonLabel(label),
      withProgressBar: withProgressBar => this.setWithProgressBar(withProgressBar),
      getLastPage: () => getPathnameEnding(this.props.pages[this.state.index].pathname),
      hideNavigation: () => this.setState({ hideNavigation: true }),
      showNavigation: () => this.setState({ hideNavigation: false })
    }

    /**
     * Allows consumers of the HOC to attach additional logic to determine if back/forward navigation should
     * in fact trigger page navigation. Handlers should return truthy values to trigger page navigation and
     * return falsey values to prevent it.
     * @param {Object} cfg - Object containing one or more handlers to amend navigation logic.
     * @param {Boolean} [ignoreCdi] - If true, cdi logic will not be implemented
     * @param {Function} [cfg.forward] - Handler for next page action.
     * @param {Function} [cfg.back] - Handler for prev page action.
     */
    amendNavigation(cfg) {
      const {ignoreCdi, ...handlers} = cfg
      if(this.state.hasAmendedNavigation) {
        this.resetNavigationHandlers()
      }
      for(let k in handlers) {
        const handler = handlers[k]
        if(isFunction(handler)) {
          const amendKey = `_${k}` // new key to original navigation handler
          this[amendKey] = this[k]
          this[k] = () => {
            const shouldNavigate = handler()
            if(shouldNavigate) {
              this[amendKey]()
            } else if(!ignoreCdi) {
              const nextCdi = this.determineNextCdi(k)
              this.updateCdi(nextCdi, push)
            }
          }
        }
        this.navigator[k] = () => this[k]()
      }
      if(!ignoreCdi) {
        const cdi = this.getCdiFromQueryString(this.props.location.search)
        // if cdi isn't set via querystring, figure out what it should be
        if(!isInteger(cdi)) {
          if(isInteger(this.state.index)) {
            // wait for index update actions to complete before cdi calc.
            this.initCdiAfterIndexUpdate = true
          } else {
            const nextCdi = this.determineNextCdi()
            this.updateCdi(nextCdi, replace)
          }
        }
      }
      this.setState({hasAmendedNavigation: true})
    }

    back() {
      let shouldBypass
      let destinationIndex = this.state.index - 1
      do {
        shouldBypass = this.executeBypassCheck(destinationIndex)
        if(shouldBypass) {
          destinationIndex -= 1
        }
      } while(shouldBypass)
      this.goToPage(destinationIndex, 'back')
    }

    bootstrapNavigation() {
      this.setIndex(this.getActivePageIndex())
      this.configureStepNumber()
      this.updateNavigator('cdi', this.state.cdi)
      // bind handler to onpopstate
      window.onpopstate = () => this.setIndex(this.getActivePageIndex())
    }

    componentDidMount() {
      this.bootstrapNavigation()
    }

    componentDidUpdate(prevProps, prevState) {
      if(prevState.cdi !== this.state.cdi) {
        this.updateNavigator('cdi', this.state.cdi)
      }

      if(prevState.index !== this.state.index) {
        this.isLastDirectionBack = this.state.index < prevState.index
        if(this.initCdiAfterIndexUpdate) {
          const nextCdi = this.determineNextCdi()
          this.updateCdi(nextCdi, replace)
          this.initCdiAfterIndexUpdate = false
        }
      }

      if(prevProps.location !== this.props.location) {
        const {location: {search}} = this.props
        this.setIndex(this.getActivePageIndex())
        this.configureStepNumber()
        if(search && (prevProps.location.search !== search)) {
          const cdi = this.getCdiFromQueryString(search)
          const prevCdi = this.getCdiFromQueryString(prevProps.location.search)
          this.setState({cdi})
          this.isLastDirectionBack = cdi < prevCdi
        } else if(this.state.hasAmendedNavigation) {
          this.resetNavigationHandlers()
        }
      }
    }

    componentWillUnmount() {
      window.onpopstate = noop
      if(isFunction(config.cleanup)) {
        config.cleanup(this.props.dispatch)
      }
    }

    configureStepNumber() {
      this.pagesToVisit = this.getPagesToVisit()
      this.setStepNumber(this.getStepNumber())
    }

    /**
     * On pages where a cdi is used, this determines what the next cdi value should
     * be based on navigation or app state. The direction is passed as arg when
     * using amended handlers (see amendNavigation). When direction is undefined
     * uses the app state to help determine what the cdi should be. It will look
     * to the query string to make determination if needed falls back to 0 in its absence.
     * @param [direction] {String} - If 'back', specifies that cdi should be decremented.
     * @return {Number}
     */
    determineNextCdi(direction) {
      let nextCdi
      const {cdi} = this.state
      const {counselingSessionData: {topThreeFactors}, location} = this.props
      if(direction) { // via amended handler
        nextCdi = direction === 'back' ? cdi - 1 : cdi + 1
      } else {
        if(this.isLastDirectionBack === null) { // => page refresh
          nextCdi = this.getCdiFromQueryString(location.search)
        } else if(this.isLastDirectionBack) {
          const isComparingImportancePage = this.isCurrentPage('comparing_importance')
          const len = topThreeFactors.length
          if((isComparingImportancePage && len > 2) || !isComparingImportancePage) {
            nextCdi = len - 1
          }
        }
      }
      return nextCdi || 0
    }

    dispatchNavigationAction(pathname, shouldGotoNextPage) {
      const {dispatch, pages, sessionId} = this.props
      const action = {
        type: actions.COUNSELING_SESSION_NAVIGATION,
        payload: {
          lastScreenSaved: getPathnameEnding(pages[this.state.index].pathname),
          pathname,
          sessionId,
          shouldGotoNextPage
        }
      }
      dispatch(action)
    }

    /**
     * Executes predicate on navigation as defined via hoc config.
     * If predicate passes and onBypass function is defined, will
     * also execute onBypass.
     * @param pageIndex - The index of the page we intend to
     *  navigate to.
     * @returns {boolean} shouldByPass - result of predicate execution
     *  if predicate defined, false otherwise.
     */
    executeBypassCheck(pageIndex) {
      let shouldBypass = false
      const {counselingSessionData, dispatch, pages} = this.props
      const destination = pages[pageIndex]
      const pathnameEnding = getPathnameEnding(destination.pathname)
      const bypassEntry = config.bypass && config.bypass[pathnameEnding]
      if(isObject(bypassEntry) && isFunction(bypassEntry.predicate)) {
        shouldBypass = bypassEntry.predicate(counselingSessionData)
        if(shouldBypass && isFunction(bypassEntry.onBypass)) {
          bypassEntry.onBypass(counselingSessionData, dispatch)
        }
      }
      return shouldBypass
    }

    forward() {
      let destination, shouldBypass
      if(this.isLastPage()) {
        this.handleLastPageNavigation()
      } else {
        destination = this.state.index + 1
        do {
          shouldBypass = this.executeBypassCheck(destination)
          if(shouldBypass) destination += 1
        } while(shouldBypass)
        this.goToPage(destination, 'forward')
      }
    }

    getActivePageIndex() {
      const {location, pages} = this.props
      return pages.findIndex(el => el.pathname === location.pathname)
    }

    getCdiFromQueryString(qs) {
      const cdi = +(getQueryStrParamVal(qs, 'cdi'))
      if(isInteger(cdi)) return cdi
    }

    /**
     * Gets pathnames of form pages that will not be skipped. This is used
     * for determining the step numbers dynamically.
     * @returns {String[]} - Each entry is the pathname of a form page, and it's
     *  array position corresponds to it's step number - 1.
     */
    getPagesToVisit() {
      // exclude pages that exclude progress bar. They are not numbered steps.
      let pagesCopy = this.props.pages.filter(page => !(page.data && page.data.excludeProgressBar))
      if(Object.keys(config.bypass)) {
        const predicates = mapValues(config.bypass, pathname => pathname.predicate)
        forIn(predicates, (predicate, pathname) => {
          if(predicate(this.props.counselingSessionData)) {
            let pageToRemoveIndex = pagesCopy.findIndex(page => page.pathname === `/session/${pathname}`)
            pagesCopy.splice(pageToRemoveIndex, 1)
          }
        })
      }
      return pagesCopy.map(page => page.pathname)
    }

    getNormalizedPageProgressValue() {
      // some pages exclude the progress bar and should not factor into calc.
      const progressPageCount = this.props.pages.filter(
        p => !p.data || (p.data && !p.data.excludeProgressBar)
      ).length
      const excludePageCount = this.props.pages.length - progressPageCount
      return Math.floor(((this.state.index + 1) - excludePageCount) / progressPageCount * 100)
    }

    getStepNumber() {
      const {pathname} = this.props.location
      return this.pagesToVisit.indexOf(pathname)
    }

    /**
     * Determines pathname. Calls dispatcher.
     * @param page {string|number} - If string, value should represent
     *  a pathname. If a number, this value represents a page index and
     *  therefore should be between 0 and pages.length - 1
     */
    goToPage(page, direction) {
      const {pages} = this.props
      const pathname = isInteger(page) ? pages[page].pathname : page
      const shouldGotoNextPage = direction === 'forward'
      this.dispatchNavigationAction(pathname, shouldGotoNextPage)
    }

    handleLastPageNavigation() {
      const {lastly} = config
      if(isFunction(lastly.beforeNavigation)) {
        lastly.beforeNavigation(this.props.dispatch)
      }
      this.goToPage(lastly.goesTo)
    }

    isBackDisabled() {
      const {index, isBackDisabledByConsumer} = this.state
      return index === 0 || isBackDisabledByConsumer
    }

    /**
     * Determine if pageId (pathname ending) matches current page
     * @param pageId {String}
     * @returns {boolean}
     */
    isCurrentPage(pageId) {
      return getPathnameEnding(this.props.pages[this.state.index].pathname) === pageId
    }

    isForwardDisabled() {
      // console.log("is forward disabled")
      return !this.props.isSessionComplete && this.state.isForwardDisabledByConsumer
    }

    isLastPage() {
      return this.state.index === this.props.pages.length - 1
    }

    // We want to delay the rendering of the navigation until the wrapped component
    // is rendered. We verify that rendering occurred by attaching a ref to the
    // wrapped component and checking that `current` property exists. We delay
    // so wrapped component can configure navigation before it is rendered, otherwise
    // we see the original nav transition to the re configured nav.
    render() {
      const isWrappedComponentBound = !!this.wrappedComponentRef.current
      const { hideNavigation } = this.state
      const {classes, ...rest} = this.props
      return (
        <Grid container direction="column" style={{flexWrap: 'nowrap'}}>
          <Grid item xs>
            <WrappedComponent
              navigator={this.navigator}
              ref={this.wrappedComponentRef}
              {...rest}
            />
          </Grid>
          {!hideNavigation && (
            <Grid
              className={classes.navContainer}
              container
              item
              justify="center"
              xs="auto"
            >
              {isWrappedComponentBound && (
                this.renderNavigation()
              )}
            </Grid>
          )}
        </Grid>
      )
    }

    renderNavigation() {
      const {forwardButtonLabel, isBackHidden, isForwardHidden, withProgressBar} = this.state
      const {classes, width, t} = this.props
      const isBackDisabled = this.isBackDisabled()
      const isForwardDisabled = this.isForwardDisabled()
      const progressValue = this.getNormalizedPageProgressValue()
      return (
        <Grid
          container
          item
          xs={isWidthUp('sm', width) ? 9 : 12}
        >
          {withProgressBar && (
            <Grid item xs={12}>
              <LinearProgress
                classes={{root: classes.linearProgressRoot}}
                color="secondary"
                value={progressValue}
                variant="determinate"
              />
            </Grid>
          )}
          {!isBackHidden && (
            <Grid item xs={6}>
              <Button
                className={
                  `${classes.navButton} ${!withProgressBar ? 'borderTop' : ''}`
                }
                color="primary"
                disabled={isBackDisabled}
                onClick={() => this.back()}
              >
                <ArrowBack className={classes.iconLeft}/> {t('Back')}
              </Button>
            </Grid>
          )}
          {!isForwardHidden && (
            <Grid
              className={classes.navButtonDivider}
              item
              style={isBackHidden ? {borderLeftWidth: 0} : null}
              xs={isBackHidden ? 12 : 6}
            >
              <Button
                className={
                  `${classes.navButton} ${!withProgressBar ? 'borderTop' : ''}`
                }
                color="primary"
                disabled={isForwardDisabled}
                onClick={() => this.forward()}
              >
                {t(`${forwardButtonLabel}`)} <ArrowForward className={classes.iconRight}/>
              </Button>
            </Grid>
          )}
        </Grid>
      )
    }

    resetNavigationVisibilityAndLabel() {
      this.setState({
        isBackHidden: false,
        forwardButtonLabel: WithNavigation.DEFAULT_NEXT_LABEL,
        withProgressBar: false,
        isForwardHidden: false
      })
    }

    /**
     * Resets navigation back/forward handlers to original instances.
     * Since handlers persist across pages, any changes made to handlers
     * via amendNavigation would persist, so we need to reset.
     */
    resetNavigationHandlers() {
      for(let amendKey of ['_back', '_forward']) {
        if(isFunction(this[amendKey])) {
          this[amendKey.slice(1)] = this[amendKey]
          this[amendKey] = null
        }
      }
      this.setState({
        cdi: null,
        hasAmendedNavigation: false
      })
    }

    setForwardButtonLabel(forwardButtonLabel) {
      this.setState({forwardButtonLabel})
    }

    setIsBackButtonHidden(isBackHidden) {
      this.setState({isBackHidden})
    }

    setIsForwardButtonHidden(isForwardHidden) {
      this.setState({isForwardHidden})
    }

    setIndex(index) {
      this.setState({index})
    }

    setIsBackDisabledByConsumer(isBackDisabledByConsumer) {
      this.setState({isBackDisabledByConsumer})
    }

    setIsForwardDisabledByConsumer(isForwardDisabledByConsumer) {
      this.setState({isForwardDisabledByConsumer})
    }

    setStepNumber(stepNumber) {
      this.setState({stepNumber})
    }

    setWithProgressBar(withProgressBar) {
      this.setState({withProgressBar})
    }

    /**
     * Sets cdi on state, dispatches an action creator to update history.
     * @param cdi {Number (Int)}
     * @param actionCreator {Function} - push || replace from react-router-redux
     */
    updateCdi(cdi, actionCreator) {
      this.setState({cdi}, () => {
        this.props.dispatch(actionCreator({
          search: `?cdi=${cdi}`
        }))
      })
    }

    updateNavigator(key, val) {
      this.navigator = {
        ...this.navigator,
        [key]: val
      }
    }
  }

  const mapStateToProps = state => ({
    counselingSessionData: state.counselingSession.counselingSessionData,
    pages: state.counselingSession.pages,
    sessionId: state.counselingSession.sessionId,
  })

  return compose(
    // we aren't using mapDispatchToProps in this component. When omitted
    // react-redux binds dispatch to props which is desired so we can pass to
    // config bypass functions.
    connect(mapStateToProps),
    translate(),
    withStyles(styles),
    withWidth(),
  )(WithNavigation)
}

export default withNavigation



// WEBPACK FOOTER //
// ./src/components/counseling-session/withNavigationHOC.js